import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileSignature, Plus, Search, AlertCircle, Settings } from 'lucide-react';
import { adminContractsAPI } from '../../services/api';
import { formatDate } from '../../utils/format';
import { useModuleEntitled } from '../../context/featureFlagStore';
import NotInPlan from '../../components/common/NotInPlan';

/**
 * Admin → Contracts: MSAs, SOWs, order forms and policies sent for
 * signature. A contract here is an uploaded PDF; the page for one
 * (AdminContractDetailPage) is where it is prepared, sent, countersigned,
 * checked and filed.
 */

export const STATUS_STYLES = {
  draft: ['Draft', 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200'],
  sent: ['Waiting for signatures', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  signed: ['Ready to countersign', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  completed: ['Completed', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
  declined: ['Declined', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'],
  voided: ['Voided', 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'],
};

export const KIND_LABELS = { msa: 'MSA', sow: 'SOW', order: 'Order form', nda: 'NDA', policy: 'Policy', other: 'Other', quote: 'Quote' };

export function StatusBadge({ status, expired, held }) {
  if (held) return <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200">Waiting for the contract before it</span>;
  if (expired) return <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Link expired</span>;
  const [label, cls] = STATUS_STYLES[status] || [status, ''];
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

const FILTERS = [['open', 'Open'], ['signed', 'To countersign'], ['completed', 'Completed'], ['', 'All']];

export default function AdminContractsPage() {
  const navigate = useNavigate();
  const entitled = useModuleEntitled('contracts');
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-contracts', status, q],
    queryFn: () => adminContractsAPI.list({ status: status || undefined, q: q || undefined }).then((r) => r.data.contracts),
    enabled: entitled,
  });

  if (!entitled) return <NotInPlan feature="Contracts" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2"><FileSignature className="w-6 h-6" /> Contracts</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Send an MSA, SOW, order form or policy for signature. Each signer confirms it is them with a code by email; you countersign; everyone gets the sealed copy, and it is filed in ConnectWise.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/contracts/settings" className="btn-secondary inline-flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</Link>
          <Link to="/admin/contracts/new" className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New contract</Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          {FILTERS.map(([value, label]) => (
            <button key={value} onClick={() => setStatus(value)}
              className={`px-3 py-1.5 text-sm ${status === value ? 'bg-primary-600 text-white' : 'bg-white text-neutral-700 hover:bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Title or company" className="input pl-9 w-64" />
        </div>
      </div>

      {error && (
        <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200">
          <AlertCircle className="w-4 h-4 mt-0.5" /> {error.response?.data?.error || 'Could not load contracts.'}
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/40 text-left text-neutral-500 dark:text-neutral-400">
            <tr>
              <th className="px-4 py-3 font-medium">Contract</th>
              <th className="px-4 py-3 font-medium">Company</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Signed</th>
              <th className="px-4 py-3 font-medium">Sent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-neutral-500">Loading…</td></tr>}
            {!isLoading && !data?.length && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500 dark:text-neutral-400">
                Nothing here yet. <Link to="/admin/contracts/new" className="text-primary-600 hover:underline">Send your first contract</Link>.
              </td></tr>
            )}
            {data?.map((c) => (
              <tr key={c.id} onClick={() => navigate(`/admin/contracts/${c.id}`)} className="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/40">
                <td className="px-4 py-3">
                  <div className="font-medium text-neutral-900 dark:text-white">{c.title}</div>
                  <div className="text-xs text-neutral-500">{KIND_LABELS[c.kind] || c.kind}{c.version > 1 ? ` · v${c.version}` : ''}{c.cw_file_error ? ' · not filed in ConnectWise' : ''}{['draft', 'ready', 'failed'].includes(c.agreement_status) ? ' · agreement to create' : ''}</div>
                </td>
                <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">{c.company_name || `#${c.cw_company_id}`}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} expired={c.expired} held={c.held_until_parent} /></td>
                <td className="px-4 py-3 tabular-nums text-neutral-700 dark:text-neutral-300">{c.signed} of {c.signers}</td>
                <td className="px-4 py-3 text-neutral-500">{c.sent_at ? formatDate(c.sent_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
